<template>
  <div class="campsite">
    <div
      class="campsite__inner white-box"
      :class="[
        { active: isActive },
        campsite.amount > 0 ? '' : 'campsite__inner--disabled',
      ]"
    >
      <div class="row">
        <div class="col-12 col-md-4">
          <div
            class="position-relative image-slider__slider"
            @click.stop.prevent="campsite.images.length && openImageModal()"
          >
            <template
              v-if="campsite.images.length && campsite.images.length > 1"
            >
              <ci-slider
                :options="sliderConfig"
                @moved="onSliderMoved"
                @init="onSliderInit"
              >
                <div class="glide__track h-100" data-glide-el="track">
                  <ul class="glide__slides h-100 m-0 p-0">
                    <li
                      v-for="(image, idx) in campsite.images"
                      :key="idx"
                      class="glide__slide position-relative h-100"
                    >
                      <img
                        :key="idx"
                        :draggable="false"
                        :alt="image.title"
                        :src="image.variants['4_3'].url"
                        class="img-fluid"
                      />
                    </li>
                  </ul>
                </div>
              </ci-slider>

              <div class="glide__arrows" data-glide-el="controls">
                <div
                  v-if="campsite.images.length > 1"
                  data-glide-dir="<"
                  class="
                    image-slider__image-navigation
                    image-slider__image-navigation--prev
                    d-flex
                    align-items-center
                    position-absolute
                    h-100
                    px-2
                  "
                  @click.prevent.stop="onPreviousImage"
                >
                  <iconArrowWhite class="icon icon--arrow"></iconArrowWhite>
                </div>
                <div
                  v-if="campsite.images.length > 1"
                  data-glide-dir=">"
                  class="
                    image-slider__image-navigation
                    image-slider__image-navigation--next
                    d-flex
                    align-items-center
                    position-absolute
                    h-100
                    px-2
                  "
                  @click.prevent.stop="onNextImage"
                >
                  <iconArrowWhite class="icon icon--arrow"></iconArrowWhite>
                </div>
              </div>
              <div
                v-if="campsite.images.length > 1"
                class="image-slider__image-gradient position-absolute"
              >
                <div
                  class="
                    image-slider__image-pagination
                    d-flex
                    justify-content-center
                    align-items-center
                    position-absolute
                  "
                >
                  <span
                    v-for="(img, idx) in campsite.images"
                    :key="idx"
                    class="image-slider__image-pagination-dot"
                    :class="{
                      'image-slider__image-pagination-dot--active':
                        activeImage - 1 === idx,
                    }"
                    >{{ idx + 1 }}</span
                  >
                </div>
              </div>
            </template>
            <!-- only one image -->
            <img
              v-if="campsite.images.length === 1"
              :alt="campsite.images[0].title"
              :src="campsite.images[0].variants['4_3'].url"
              class="image-slider__image-single"
            />

            <!-- fallback image -->
            <div
              v-if="!campsite.images.length"
              class="image-slider__image--fallback"
            >
              <iconFallbackImage class="icon"></iconFallbackImage>
            </div>
          </div>
        </div>
        <div class="col-12 col-md-8 py-0 py-md-3">
          <div class="row h-100 pr-0 pr-md-3">
            <div
              class="col-12 col-md-12"
            >
              <div class="campsite__meta">
                <h3 class="meta__headline">{{ campsite.name }}</h3>
                <div v-if="!showMoreDesc">
                  <div
                    class="meta__description"
                    v-html="shortDescription"
                  ></div>
                  <div
                    v-if="
                      campsiteDescriptionText.length >= 140 && !showMoreDesc
                    "
                    @click.stop="toggleMoreLessDesc(true)"
                    class="show__more"
                  >
                    + {{ $t(`generic.labels.showMore`) }}
                  </div>
                </div>
                <div v-if="showMoreDesc">
                  <div
                    class="meta__description"
                    v-html="campsite.description"
                  ></div>
                  <div
                    @click.stop="toggleMoreLessDesc(false)"
                    class="show__less"
                  >
                    - {{ $t(`generic.labels.showLess`) }}
                  </div>
                </div>
                <div v-if="campsite.is_free_cancellation"> 
                  <div class="show__more pe-none">
                    {{ $t(`pages.cancellationTerms.headline`) }}
                  </div>
                  {{ $t('pages.cancellationTerms.freeCancellationText') }}
                </div>
                <a class="cancellation__terms__link" v-else @click="campsite.amount > 0 && openCancellationTermsModal(campsite.cancellation_terms)">
                  <span>{{ $t(`pages.checkoutStepTwo.viewCancellationTerms`) }} </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <div v-for="(choice, idx) in campsite.choices" :key="idx" class="campsite__choice">
            <div class="campsite__choice-details">
              <div class="campsite__choice-info">
                <div v-if="choice.type === 'campingcard-acsi'">
                  <div class="campsite__choice-label">
                    <div class="campsite__choice-badge">
                     {{ $t(`pages.checkoutStepTwo.campingCard.campingCardLabel`) }}
                    </div>
                    <CiCampingCareAcsiInfo :campground="campground" :currency="currency" :language="language"/>
                  </div>
                  <p class="campsite__choice-label-text">{{ choice.label }}</p>
                  <p class="campsite__choice-description">{{ $t(`pages.checkoutStepTwo.campingCard.copy`) }}</p>
                </div>
                <p class="campsite__choice-normal" v-else>{{ $t(`pages.checkoutStepTwo.campingCard.normalTariff`) }}</p>
              </div>
              <div class="campsite__choice-actions">
                <div class="campsite__choice-price">
                  <span class="sum-value"> 
                    {{ choice.price.value | currency(campground.currency, language.code) }}
                  </span>
                  <span class="sum-value-alt" v-if="choice.price.value_alt">
                    {{ choice.price.value_alt | currency(currency.code, language.code) }}
                  </span>
                </div>
                <CiButton
                  class="navigation__button button--primary"
                  :disabled="campsite.amount <= 0"
                  @click="nextStep(choice.type ? choice.type : 'default')"
                >
                  <template v-if="status && !status.loading">
                    <span>{{ $t('generic.buttons.nextStep') }}</span>
                    <iconArrowWhite class="icon icon--animated"></iconArrowWhite>
                  </template>
                  <template v-if="status && status.loading">
                    <span>{{ $t('generic.buttons.loading') }}</span>
                    <span class="spinner"></span>
                  </template>
                </CiButton>
              </div>
            </div>
          </div>
        </div>

      </div>
      <div class="row" v-if="campsite.amount <= 0">
        <div class="col-12">
          <div class="campsite__reason">
            <div class="has-icon">
              <iconWarning class="icon"></iconWarning>
            </div>
            <div class="reason-title">
              {{ $t(`pages.checkoutStepTwo.campsiteNotAvailableTitle`) }}
            </div>
            <div class="reason-content" v-if="campsite.reason">
              {{
                $t(
                  `pages.checkoutStepTwo.campsiteNotAvailableReasons.${campsite.reason}`
                )
              }}
              <span
                v-if="
                  campsite.reason === 'invalidArrivalAndDepartureWeekday' ||
                    campsite.reason === 'invalidArrivalWeekday' ||
                    campsite.reason === 'invalidDepartureWeekday'
                "
              >
                <span class="restricted-weekdays">
                  <span
                    v-if="
                      campsite.reason === 'invalidArrivalAndDepartureWeekday' ||
                        campsite.reason === 'invalidArrivalWeekday'
                    "
                    v-html="
                      $t('pages.checkoutStepTwo.restrictedArrivalWeekdays', {
                        days: getWeekdaysString(campsiteArrivalWeekdays),
                      }) + ' '
                    "
                  ></span>
                  <span
                    v-if="
                      campsite.reason === 'invalidArrivalAndDepartureWeekday' ||
                        campsite.reason === 'invalidDepartureWeekday'
                    "
                    v-html="
                      $t('pages.checkoutStepTwo.restrictedDeparturelWeekdays', {
                        days: getWeekdaysString(campsiteDepartureWeekdays),
                      }) + ' '
                    "
                  ></span>
                  <a
                    class="action"
                    href="#"
                    @click="
                      $emit('to-step', {
                        number: 1,
                        key: 'displaySelection',
                        value: 'dates',
                      })
                    "
                    >{{ $t('pages.checkoutStepTwo.changeTravelDates') }}</a
                  >
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  CiProgressNavigation,
  CiSlider,
  CiImageModal,
  CiButton,
  CiCancellationTermsModal,
} from '@/components/index'

import iconCheckmarkWhite from '@/assets/icons/checkmark-white.svg'
import iconLock from '@/assets/icons/lock-solid.svg'
import iconWarning from '@/assets/icons/warning-orange.svg'
import iconArrowWhite from '@/assets/icons/arrow-white-dynamic.svg'
import iconFallbackImage from '@/assets/icons/image-line.svg'
import iconArrowBlack from '@/assets/icons/arrow-black.svg'
import iconCampingCard from '@/assets/icons/camping-card.svg'
import { mapActions } from 'vuex'
import CiCampingCareAcsiInfo from '../CiCampingCareAcsiInfo/CiCampingCareAcsiInfo'

export default {
  name: 'CiAccommodation',

  components: {
    CiImageModal,
    CiProgressNavigation,
    CiSlider,
    iconArrowWhite,
    iconCheckmarkWhite,
    iconFallbackImage,
    iconLock,
    iconWarning,
    CiButton,
    iconArrowBlack,
    iconCampingCard,
    CiCancellationTermsModal,
    CiCampingCareAcsiInfo,
  },

  props: {
    activeStep: {
      type: Number,
      required: true,
    },

    campground: {
      type: Object,
      required: true,
    },

    campsite: {
      type: Object,
      required: true,
    },

    isActive: {
      type: Boolean,
      default: false,
    },

    steps: {
      type: Object,
      required: true,
    },

    status: {
      type: Object,
      required: true,
    },

    language: {
      type: Object,
      required: true,
    },

    currency: {
      type: Object,
      required: true,
    },

    nights: {
      type: Number,
      required: true,
    },
    selectedSeasons: {
      type: Array,
      required: true,
    },
  },
  computed: {
    policy() {
      return this.$store.getters['booking/policy']
    },
    price() {
      return this.$store.getters['booking/price']
    },
    campingcardAcsiInfo() {
      return this.$store.getters['campground/campingcardAcsiInfo']
    },
    selectedPitch() {
      return this.price.components
        ? this.price.components.find(
            component => component.object_type === 'campsite'
          )
        : 0
    },
    alternateArrivalDepartureDays() {
      return this.campsite.alternative_arrival_and_departure_days
    },
    campsiteArrivalWeekdays() {
      let alternateArrivalDepartureDay =
        this.alternateArrivalDepartureDays &&
        this.alternateArrivalDepartureDays.find(
          arrival => this.selectedSeasons[0].id === arrival.season
        )
      if (alternateArrivalDepartureDay) {
        return alternateArrivalDepartureDay.arrival_weekdays
      } else {
        return this.campsite.arrival_weekdays
      }
    },
    campsiteDepartureWeekdays() {
      let alternateArrivalDepartureDay =
        this.alternateArrivalDepartureDays &&
        this.alternateArrivalDepartureDays.find(
          departure =>
            this.selectedSeasons[this.selectedSeasons.length - 1].id ===
            departure.season
        )
      if (alternateArrivalDepartureDay) {
        return alternateArrivalDepartureDay.departure_weekdays
      } else {
        return this.campsite.departure_weekdays
      }
    },
    campsiteDescriptionText() {
      let div = document.createElement('div')
      div.innerHTML = this.campsite.description
      return div.textContent || div.innerText || ''
    },
    shortDescription() {
      if (this.campsiteDescriptionText.length >= 140) {
        return this.campsiteDescriptionText.substring(0, 140) + '...'
      } else {
        return this.campsite.description
      }
    },
    stepFulfilled() {
      return this.steps[this.activeStep].fulfilled
    },
  },

  data() {
    return {
      activeImage: 1,
      slider: null,
      sliderConfig: {
        gap: 0,
        perView: 1,
        startAt: 0,
        type: 'carousel',
      },
      showMoreDesc: false,
      // isPolicyLoader: false,
    }
  },

  methods: {
    ...mapActions('booking', {
      setSelectedBookingType: 'setSelectedBookingType',
    }),

    getWeekdaysString(days) {
      let names = []
      let idx = 0
      let hasSunday = false
      for (let day = 4; day <= 10; day++) {
        if (days.indexOf(idx) !== -1) {
          if (idx === 0) {
            hasSunday = true
          } else {
            names.push(
              new Date(1970, 0, day).toLocaleString(this.language.code, {
                weekday: 'long',
              })
            )
          }
        }
        idx++
      }
      if (hasSunday) {
        names.push(
          new Date(1970, 0, 4).toLocaleString(this.language.code, {
            weekday: 'long',
          })
        )
      }
      return names.join(', ')
    },

    onSliderInit(slider) {
      this.slider = slider
    },

    onSliderMoved(slider) {
      this.activeImage = slider.index + 1
    },

    onPreviousImage() {
      this.slider.go('<')
    },

    onNextImage() {
      this.slider.go('>')
    },

    openImageModal() {
      const settings = {
        adaptive: true,
        clickToClose: true,
        height: 'auto',
        maxWidth: 800,
        pivotY: 0.5,
        scrollable: true,
        width: '100%',
      }

      this.$modal.show(
        CiImageModal,
        {
          title: this.campsite.name,
          images: this.campsite.images,
        },
        settings,
        {}
      )
    },
    toggleMoreLessDesc(value) {
      this.showMoreDesc = value
    },
    openCancellationTermsModal(policy){
      const settings = {
          adaptive: true,
          clickToClose: true,
          height: 'auto',
          maxWidth: 800,
          pivotY: 0.5,
          scrollable: true,
          width: '100%',
        };

        this.$modal.show(
          CiCancellationTermsModal,
          {
            title: this.$t('pages.cancellationTerms.headline'),
            content: policy
          },
          settings,
          {}
        );
      // this.isPolicyLoader = false;
    },

    nextStep(type) {
      this.setSelectedBookingType(type);
      this.$emit('select-campsite', this.campsite);
      this.$router.push({
        name: 'Step',
        params: { ...this.$route.params, step: this.activeStep + 1 },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.campsite {
  margin: 0 0 20px;
}

.flex_row {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
}

.campsite__inner {
  margin: 0;
  padding: 0;
  position: relative;
  transition: border 0.4s ease;

  &.campsite__inner--disabled {
    color: #9b9b9b;
    background-image: repeating-linear-gradient(
      45deg,
      #f6f6f6,
      #f6f6f6 5px,
      #fcfcfc 5px,
      #fcfcfc 20px
    );
    border: 1px solid rgba(155, 155, 155, 0.5);
    padding-bottom: 0;

    .no-touch &:hover {
      border: 1px solid rgba(155, 155, 155, 0.5);
    }
  }

  &.active {
    border-color: $primary-green;
  }
}

.campsite__reason {
  @include clearfix;
  @include font-format(0.75rem, 1.25rem);

  color: black;
  background-color: #fefcdd;
  padding: 12px 15px;
  margin: 0;

  .icon {
    width: 20px;
    height: auto;
  }

  & > div {
    float: left;
  }

  .has-icon {
    width: 40px;
  }
  .reason-title {
    @include font('gotham_mediumregular');

    width: 150px;
  }
  .reason-content {
    width: 100%;
    margin-top: 10px;
    @include media-breakpoint-up(sm) {
      width: calc(100% - 190px);
      margin-top: 0;
    }

    .action {
      color: $primary-orange;
      text-decoration: underline;
      font-weight: bold;

      &:hover {
        text-decoration: none;
      }
    }
  }
}

.campsite__status {
  align-items: center;
  display: flex;
  flex: 1;
  height: 100%;
  justify-content: flex-end;
  padding: 20px 20px 20px 0;

  @include media-breakpoint-up(md) {
    height: auto;
    justify-content: center;
    padding: 0;
  }

  .status__circle {
    align-items: center;
    background: #cdcccd;
    border-radius: 50%;
    display: flex;
    height: 30px;
    justify-content: center;
    opacity: 0.5;
    width: 30px;
    transition: background-color 0.6s ease;
    align-self: flex-end;

    .icon {
      width: 17px;
      height: auto;
    }

    &.status__circle--active {
      opacity: 1;
      background: $primary-green;
    }

    @include media-breakpoint-up(md) {
      height: 45px;
      width: 45px;

      .icon {
        width: 27px;
        height: auto;
      }
    }
  }

  .warning {
    @include font('gotham_mediumregular');
    @include font-format(0.625rem, 1rem);

    position: relative;
    background-color: #fefcda;
    border-radius: 8px;
    color: #f5a623;
    padding: 8px 32px 6px 12px;
    text-transform: uppercase;

    .icon {
      position: absolute;
      top: 50%;
      right: 0;
      transform: translate(-10px, -50%);
      width: 11px;
      height: auto;
      fill: #f5a623;
    }
  }
}

.campsite__meta {
  border-bottom: 1px solid hsla(0, 0%, 60.8%, 0.5);
  flex: 3;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  height: 100%;

  @include media-breakpoint-up(md) {
    border: 0;
    padding: 0;
  }

  .meta__headline {
    @include font-format(1rem, 1.625em);

    margin-bottom: 15px;
  }

  .meta__description {
    @include font-format(1rem, 1.25rem);

    margin: 0;
    padding: 0;
    margin-bottom: 15px;
  }
  .show__more,
  .show__less {
    @include font-format(0.9rem, 1.1rem);

    color: #000000;
    font-weight: 600;
    margin-top: 16px;
    cursor: pointer;
  }
  .show__less {
    text-decoration: underline;
    margin-top: 16px;
  }
}

.campsite__price {
  @include font('gotham_mediumregular');
  @include font-format(1.375rem, 1.625em);

  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10px 0 10px 20px;

  @include media-breakpoint-up(md) {
    padding: 0 20px 0 0;
    align-items: center;
  }

  .price__value--alt {
    @include font-format(1rem, 1.25em);

    color: #9b9b9b;
    margin: 0 0 5px 0;
  }

  .price__meta {
    @include font('gotham_mediumregular');
    @include font-format(0.625rem, 1.625em);

    color: #9b9b9b;
  }

  .nights__discount {
    @include font('gotham_mediumregular');
    @include font-format(0.625rem, 1.625em);

    color: #707070;
    font-size: 0.8rem;
    font-weight: 100;
  }

  .price__striked {
    font-size: 1rem;
    font-weight: 100;
    text-decoration: line-through;
    margin-right: 0.2rem;
    color: grey;
  }

  .discounted__price {
    font-size: 1rem;
    font-weight: 500;
  }
}

.cancellation__terms__link {
  margin-top: auto;
  margin-left: auto;
  margin-bottom: 0;
  color: #007bff !important;
  text-decoration: none;
  cursor: pointer;
}
.cancellation__terms__link:hover{
  color: #0056b3;
  text-decoration: underline;
}


.campsite__inner--disabled .cancellation__terms__link{
  color: #9b9b9b !important;
  cursor: default;
}

.campsite__choice {
  padding: 10px 15px;
  border-top: 1px solid #ccc;
}

.campsite__choice-details {
  display: flex;
  flex-direction: column;
  justify-content: normal;
  align-items: normal;

  @include media-breakpoint-up(sm) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
  .button--primary{
    margin: 5px 0;

    @include media-breakpoint-up(sm) { 
      margin: 15px 0; 
    }
  }
}

.campsite__choice-info {
  display: flex;
  flex-direction: column;
}

.campsite__choice-label {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.campsite__choice-badge {
  background: #00a4ee;
  color: white;
  padding: 0px 7px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 10rem;
  height: 18px;
  
}

.campsite__choice-description {
  margin-bottom: 0;
  font-size: 0.7rem;
  line-height: 0.7rem;
  padding: 3px 0;
  margin-top: 0;
}

.campsite__choice-label-text {
  font-size: 1rem;
  margin-bottom: 0;
}

.campsite__choice-normal {
  margin-bottom: 0;
}

.campsite__choice-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.campsite__choice-price {
  width: 150px;
  text-align: center;
  .sum-value {
    font-family: gotham_mediumregular, Avenir, Helvetica, Arial, sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 1.175rem;
    line-height: 1em;
  }
   .sum-value-alt {
    line-height: 1.25em;
    color: #9b9b9b;
    margin-top: 3px;
   }
}

.campsite__choice-button {
  width: 180px;
  padding: 0 30px;
}

.icon--animated {height: 14px;}

.campsite__inner--disabled .meta__description, 
.campsite__inner--disabled .show__more {
  color: #9b9b9b !important;
  cursor: default;
}

.campsite__inner--disabled .campsite__choice-badge {
  opacity: 0.5;
  cursor: default;
}


</style>
